import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { applyServiceWorkerUpdate, unregisterExistingServiceWorkers } from '../../utils/serviceWorkerRegistration';
import { usePwaInstallPrompt } from './PwaInstallContext';
import './pwa.css';

export function notifyPwaUpdateAvailable(registration) {
  window.dispatchEvent(new CustomEvent('member-nest:pwa-update', { detail: { registration } }));
}

const UPDATE_RELOAD_FALLBACK_MS = 3500;
const UPDATE_RELOAD_PARAM = 'mn-sw-update';
const UPDATE_RESET_PARAM = 'mn-sw-reset';
const UPDATE_ATTEMPT_KEY = 'memberNest.pwaUpdateAttemptAt';
const STUCK_UPDATE_WINDOW_MS = 10 * 60 * 1000;

function getDismissTarget(registration) {
  if (!registration || typeof registration !== 'object') return null;
  return registration.waiting && typeof registration.waiting === 'object'
    ? registration.waiting
    : registration;
}

function getReloadUrl(param = UPDATE_RELOAD_PARAM) {
  const url = new URL(window.location.href);
  url.searchParams.delete(UPDATE_RELOAD_PARAM);
  url.searchParams.delete(UPDATE_RESET_PARAM);
  url.searchParams.set(param, String(Date.now()));
  return url.toString();
}

function hasUpdateReloadParam() {
  try {
    return new URL(window.location.href).searchParams.has(UPDATE_RELOAD_PARAM);
  } catch (err) {
    return false;
  }
}

function rememberUpdateAttempt() {
  try {
    window.sessionStorage.setItem(UPDATE_ATTEMPT_KEY, String(Date.now()));
  } catch (err) {
    // Ignore storage failures; the URL parameter is the fallback signal.
  }
}

function clearUpdateAttempt() {
  try {
    window.sessionStorage.removeItem(UPDATE_ATTEMPT_KEY);
  } catch (err) {
    // Ignore storage failures.
  }
}

function hasRecentUpdateAttempt() {
  try {
    const attemptedAt = Number(window.sessionStorage.getItem(UPDATE_ATTEMPT_KEY) || 0);
    return Number.isFinite(attemptedAt) && Date.now() - attemptedAt < STUCK_UPDATE_WINDOW_MS;
  } catch (err) {
    return false;
  }
}

export default function PwaUpdateToast() {
  const { user } = useAuth();
  const { isStandalone } = usePwaInstallPrompt();
  const [registration, setRegistration] = useState(null);
  const [isApplyingUpdate, setIsApplyingUpdate] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const reloadStartedRef = useRef(false);
  const reloadFallbackRef = useRef(null);
  const dismissedUpdatesRef = useRef(new WeakSet());
  const openedAfterUpdateAttemptRef = useRef(hasUpdateReloadParam());

  const clearReloadFallback = useCallback(() => {
    if (!reloadFallbackRef.current) return;
    window.clearTimeout(reloadFallbackRef.current);
    reloadFallbackRef.current = null;
  }, []);

  const reloadForUpdate = useCallback(() => {
    if (reloadStartedRef.current) return;
    reloadStartedRef.current = true;
    clearReloadFallback();
    setIsApplyingUpdate(true);
    try {
      if (typeof window.location.replace === 'function') {
        window.location.replace(getReloadUrl());
        return;
      }
    } catch (err) {
      // Fall through to the plain reload path.
    }
    window.location.reload();
  }, [clearReloadFallback]);

  useEffect(() => {
    const handleUpdate = (event) => {
      const nextRegistration = event.detail?.registration || null;
      const dismissTarget = getDismissTarget(nextRegistration);
      if (dismissTarget && dismissedUpdatesRef.current.has(dismissTarget)) return;

      reloadStartedRef.current = false;
      clearReloadFallback();
      setIsApplyingUpdate(false);
      setIsRecoveryMode(openedAfterUpdateAttemptRef.current || hasRecentUpdateAttempt());
      setRegistration(nextRegistration);
    };
    window.addEventListener('member-nest:pwa-update', handleUpdate);
    return () => window.removeEventListener('member-nest:pwa-update', handleUpdate);
  }, [clearReloadFallback]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const handleControllerChange = () => {
      reloadForUpdate();
    };
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
  }, [reloadForUpdate]);

  useEffect(() => () => clearReloadFallback(), [clearReloadFallback]);

  const handleRecovery = useCallback(async () => {
    if (isApplyingUpdate) return;
    reloadStartedRef.current = true;
    clearReloadFallback();
    setIsApplyingUpdate(true);
    try {
      await unregisterExistingServiceWorkers();
    } catch (err) {
      // Continue to a hard reload even if browser cleanup is partially blocked.
    } finally {
      clearUpdateAttempt();
      try {
        if (typeof window.location.replace === 'function') {
          window.location.replace(getReloadUrl(UPDATE_RESET_PARAM));
          return;
        }
      } catch (err) {
        // Fall through to the plain reload path.
      }
      window.location.reload();
    }
  }, [clearReloadFallback, isApplyingUpdate]);

  const handleUpdate = useCallback(() => {
    if (isApplyingUpdate) return;
    if (isRecoveryMode) {
      handleRecovery();
      return;
    }
    setIsApplyingUpdate(true);
    rememberUpdateAttempt();
    if (!applyServiceWorkerUpdate(registration)) {
      reloadForUpdate();
      return;
    }
    clearReloadFallback();
    reloadFallbackRef.current = window.setTimeout(reloadForUpdate, UPDATE_RELOAD_FALLBACK_MS);
  }, [clearReloadFallback, handleRecovery, isApplyingUpdate, isRecoveryMode, registration, reloadForUpdate]);

  const handleDismiss = useCallback(() => {
    const dismissTarget = getDismissTarget(registration);
    if (dismissTarget) dismissedUpdatesRef.current.add(dismissTarget);
    setIsRecoveryMode(false);
    setRegistration(null);
  }, [registration]);

  if (!registration || (!isStandalone && !user)) return null;

  return (
    <div className="pwa-toast" role="status" aria-live="polite">
      <div>
        <strong>Update available</strong>
        <span>{isRecoveryMode ? 'Update Member Nest to finish updating.' : 'Refresh Member Nest to use the latest version.'}</span>
      </div>
      <button type="button" className="button pwa-toast__button" onClick={handleUpdate} disabled={isApplyingUpdate}>
        {isApplyingUpdate ? 'Updating...' : 'Update'}
      </button>
      <button type="button" className="pwa-toast__dismiss" onClick={handleDismiss} aria-label="Dismiss update">
        ×
      </button>
    </div>
  );
}
